import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Container, PhoneCall } from 'lucide-react';
import { ORDER_TIERS } from '../lib/tireSizes';
import { INTL } from '../constants/testIds';

export const InternationalOrders = () => (
  <section id="international" data-testid={INTL.section} className="relative bg-brand-bg py-24 sm:py-32">
    <div className="max-w-6xl mx-auto px-5 sm:px-8 grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.6 }}
        className="lg:col-span-7"
      >
        <p className="font-mono text-xs uppercase tracking-widest text-brand-amber mb-3">Export Program</p>
        <h2 className="font-display font-extrabold uppercase tracking-tight text-brand-text text-3xl sm:text-4xl lg:text-5xl">
          International Shipping, Simplified
        </h2>
        <p className="mt-5 max-w-2xl text-brand-muted text-base sm:text-lg">
          We ship bulk tires directly to the port of your choice, anywhere in the world. We cater to both large and
          small international order loads — with doubling &amp; tripling packing available on request to maximize
          every container.
        </p>
        <p className="mt-3 max-w-2xl text-brand-muted text-sm sm:text-base">
          Every order is sourced from two origins — our Mt Vernon, Ohio warehouse and our partner suppliers in
          Germany — so large and recurring orders are never capacity-constrained on volume or grading quality.
        </p>

        <div className="mt-7 flex flex-wrap gap-3">
          {ORDER_TIERS.map((t) => (
            <span key={t.quantity} className="font-mono text-sm text-brand-amber border border-brand-border rounded-full px-4 py-1.5 bg-brand-surface">
              {t.quantity} tire minimum
            </span>
          ))}
        </div>

        <p className="mt-6 max-w-2xl text-brand-muted text-base sm:text-lg">
          Looking to ship a large volume to the port of your choice? We can supply bulk wholesale tires to match.
          Sign up below to arrange a quick 15-minute consult about your international order.
        </p>

        <div className="mt-8 flex flex-wrap gap-4">
          <a
            href="#order-form"
            data-testid={INTL.ctaConsult}
            className="inline-flex items-center gap-2 bg-brand-orange hover:bg-brand-orangeHover text-white font-display font-bold uppercase tracking-wide rounded-full px-7 py-3.5 transition-colors"
          >
            <PhoneCall className="w-4 h-4" />
            Book Your 15-Min Consult
          </a>
          <Link
            to="/international-shipping"
            className="inline-flex items-center gap-2 border border-brand-border hover:border-brand-orange text-brand-text font-display font-bold uppercase tracking-wide rounded-full px-7 py-3.5 transition-colors"
          >
            Full Shipping Process
          </Link>
          <Link
            to="/request-quote"
            data-testid={INTL.ctaWholesaleQuote}
            className="inline-flex items-center gap-2 border border-brand-amber text-brand-amber hover:bg-brand-amber hover:text-brand-bg font-display font-bold uppercase tracking-wide rounded-full px-7 py-3.5 transition-colors"
          >
            Request a Wholesale Quote
          </Link>
        </div>

        <div
          data-testid={INTL.containerNote}
          className="mt-10 rounded-2xl border border-brand-border bg-brand-surface p-6 sm:p-7 flex items-start gap-4 max-w-2xl"
        >
          <Container className="w-6 h-6 text-brand-amber mt-0.5 shrink-0" />
          <p className="text-brand-muted text-sm sm:text-base">
            Need a shipping container too? We keep containers in supply and can provide one with your order to
            streamline the entire process.
          </p>
        </div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0, scale: 0.96 }}
        whileInView={{ opacity: 1, scale: 1 }}
        viewport={{ once: true }}
        transition={{ duration: 0.6, delay: 0.15 }}
        className="lg:col-span-5 relative rounded-2xl overflow-hidden border border-brand-border min-h-[20rem] lg:min-h-[28rem]"
      >
        <img
          src="/images/brand/truck-loading-dock.jpg"
          alt="BulkUsedTires.com branded export truck loaded and ready for worldwide shipment"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-brand-bg via-brand-bg/10 to-transparent" />
      </motion.div>
    </div>
  </section>
);
